import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const PAGE_SIZES = [10, 25, 50] as const;

/**
 * Client-side pagination over an already-filtered list.
 * `resetKey` should change whenever filters/search change so the view jumps back to page 1
 * instead of staying on a page that no longer exists.
 */
export function usePagination<T>(items: T[], resetKey: unknown, initialPageSize: number = PAGE_SIZES[0]) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => { setPage(1); }, [resetKey, pageSize]);

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  // Deleting the last row of the last page shouldn't leave an empty page.
  const current = Math.min(page, pageCount);
  const pageItems = useMemo(
    () => items.slice((current - 1) * pageSize, current * pageSize),
    [items, current, pageSize],
  );

  return { page: current, setPage, pageSize, setPageSize, pageCount, pageItems, total: items.length };
}

interface PaginationProps {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Noun for the counter, e.g. "cuentas". */
  itemLabel?: string;
  className?: string;
}

/** Page numbers with ellipses: 1 … 4 5 6 … 12 */
function pageWindow(page: number, pageCount: number): (number | '…')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < pageCount - 1) pages.push('…');
  pages.push(pageCount);
  return pages;
}

export function Pagination({
  page, pageCount, pageSize, total, onPageChange, onPageSizeChange, itemLabel = 'registros', className,
}: PaginationProps) {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const btn = 'inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-sm transition-colors disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground', className)}>
      <div className="flex items-center gap-3">
        <span>
          {from}–{to} de {total} {itemLabel}
        </span>
        <label className="flex items-center gap-1.5 text-xs">
          Mostrar
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground"
          >
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      {pageCount > 1 && (
        <nav className="flex items-center gap-1" aria-label="Paginación">
          <button
            type="button"
            className={cn(btn, 'hover:bg-muted')}
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {pageWindow(page, pageCount).map((p, i) => (p === '…' ? (
            <span key={`gap-${i}`} className="px-1">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cn(btn, p === page ? 'bg-primary font-medium text-primary-foreground' : 'hover:bg-muted text-foreground')}
            >
              {p}
            </button>
          )))}
          <button
            type="button"
            className={cn(btn, 'hover:bg-muted')}
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            aria-label="Página siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  );
}
